import { useQuery } from '@tanstack/react-query'
import { API_ORIGIN } from '@/constants'
import { authorizedFetch } from '@/lib/api'
import type { CandidateMatch, SearchCandidatesResponse } from '@/hooks/use-briefs'
import type { OpportunityMatch, OpportunitySearchResponse } from '@/hooks/use-smart-search'

export type SearchSessionDirection = 'candidates' | 'opportunities'

export interface SearchSessionSummary {
  id: string
  created_at: string
  archived_at?: string | null
  archived_reason?: string | null
  direction?: SearchSessionDirection
  instructions: string
  required_skills: string[]
  preferred_skills: string[]
  location?: string | null
  experience_level?: string | null
  result_count: number
  satisfaction_score?: number | null
  strategies_used: string[]
}

export interface SearchSessionDetail {
  session_id: string
  evaluation_run_id?: string | null
  created_at: string
  previous_run_at?: string | null
  archived_at?: string | null
  archived_reason?: string | null
  direction: SearchSessionDirection
  instructions: string
  required_skills: string[]
  preferred_skills: string[]
  location?: string | null
  experience_level?: string | null
  total_candidates_evaluated: number
  llm_judged_count: number
  top_matches: CandidateMatch[]
  top_opportunity_matches: OpportunityMatch[]
  strategies_used: string[]
  search_summary?: string | null
  satisfaction_score?: number | null
}

export type UseSearchSessionsOptions = {
  direction?: SearchSessionDirection
  briefId?: string | null
  standalone?: boolean
  opportunityId?: string | null
  profileId?: string | null
  includeArchived?: boolean
  enabled?: boolean
}

export function searchSessionsQueryKey(options: UseSearchSessionsOptions) {
  const {
    direction = 'candidates',
    briefId,
    standalone = false,
    opportunityId,
    profileId,
    includeArchived = false,
  } = options
  return [
    'search-sessions',
    direction,
    briefId ?? null,
    standalone,
    opportunityId ?? null,
    profileId ?? null,
    includeArchived,
  ] as const
}

function isSearchSessionsEnabled(options: UseSearchSessionsOptions): boolean {
  const {
    direction = 'candidates',
    briefId,
    standalone = false,
    opportunityId,
    enabled = true,
  } = options
  if (!enabled) return false
  if (direction === 'opportunities') return true
  return Boolean(briefId) || standalone || Boolean(opportunityId)
}

export function useSearchSessions(options: UseSearchSessionsOptions) {
  const {
    direction = 'candidates',
    briefId,
    standalone = false,
    opportunityId,
    profileId,
    includeArchived = false,
    enabled = true,
  } = options

  return useQuery({
    queryKey: searchSessionsQueryKey(options),
    queryFn: async (): Promise<SearchSessionSummary[]> => {
      const params = new URLSearchParams()
      params.set('direction', direction)
      if (briefId) params.set('brief_id', briefId)
      if (standalone) params.set('standalone', '1')
      if (opportunityId) params.set('opportunity_id', opportunityId)
      if (profileId) params.set('profile_id', profileId)
      if (includeArchived) params.set('include_archived', '1')

      const response = await authorizedFetch(
        `${API_ORIGIN}/api/search/sessions?${params.toString()}`,
      )
      if (!response.ok) {
        throw new Error('Failed to load analysis history')
      }
      return response.json()
    },
    enabled: isSearchSessionsEnabled({ ...options, enabled }),
    staleTime: 15 * 1000,
  })
}

export function useSearchSession(sessionId: string | null) {
  return useQuery({
    queryKey: ['search-session', sessionId],
    queryFn: async (): Promise<SearchSessionDetail> => {
      if (!sessionId) throw new Error('Session ID required')

      const response = await authorizedFetch(`${API_ORIGIN}/api/search/sessions/${sessionId}`)
      if (!response.ok) {
        throw new Error('Failed to load search session')
      }
      return response.json()
    },
    enabled: Boolean(sessionId),
    staleTime: 60 * 1000,
  })
}

export function searchSessionDetailToResults(detail: SearchSessionDetail): SearchCandidatesResponse {
  return {
    session_id: detail.session_id,
    evaluation_run_id: detail.evaluation_run_id ?? undefined,
    previous_run_at: detail.previous_run_at ?? undefined,
    total_candidates_evaluated: detail.total_candidates_evaluated,
    llm_judged_count: detail.llm_judged_count,
    top_matches: detail.top_matches as CandidateMatch[],
    strategies_used: detail.strategies_used,
    search_summary: detail.search_summary ?? undefined,
  }
}

export function searchSessionDetailToOpportunityResults(
  detail: SearchSessionDetail,
): OpportunitySearchResponse {
  return {
    session_id: detail.session_id,
    evaluation_run_id: detail.evaluation_run_id ?? undefined,
    total_candidates_evaluated: detail.total_candidates_evaluated,
    llm_judged_count: detail.llm_judged_count,
    top_matches: detail.top_opportunity_matches,
    strategies_used: detail.strategies_used,
    search_summary: detail.search_summary ?? undefined,
  }
}

export function formatSearchSessionLabel(session: SearchSessionSummary): string {
  const date = new Date(session.created_at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  if (session.direction === 'opportunities') {
    const instructionPart = session.instructions?.trim()
      ? session.instructions.trim().slice(0, 40)
      : 'Profile match'
    return `${date} · ${instructionPart} · ${session.result_count} results`
  }

  const skillPart =
    session.required_skills.length > 0
      ? session.required_skills.slice(0, 3).join(', ')
      : 'No required skills'
  return `${date} · ${skillPart} · ${session.result_count} results`
}
